import { useMemo, useState } from "react";
import { Building2, Check, ChevronDown, Lock, Menu, Plus, X } from "lucide-react";
import { Link } from "wouter";

import { cn } from "@/lib/utils";
import {
  bucketProjectUpdatedAt,
  PROJECT_BUCKET_LABEL,
  type ProjectRecencyBucket,
} from "@/lib/project-recency-bucket";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { StatusDot } from "@/components/v2/shared/StatusDot";
import {
  UserProfileMenu,
  type UserInfo,
  type OrgInfo,
} from "@/components/v2/layout/UserProfileMenu";
import type { ThemePreference } from "@/lib/theme-preference";

export type { UserInfo, OrgInfo };

const BUCKET_ORDER: ProjectRecencyBucket[] = ["today", "yesterday", "earlier"];

/**
 * Sentinel id GlobalNavBar uses for the resolved "current project" when a page
 * provides none. The switcher treats it as "no real current project" — so when
 * the dropdown is gated off (ANA-266) the centre renders nothing instead of a
 * placeholder pill.
 */
export const NO_CURRENT_PROJECT_ID = "__none__";

export interface Project {
  id: string;
  name: string;
  /** Human-readable slug for `/projects/:shortId/overview` (distinct from UUID `id`). */
  short_id?: string;
  /**
   * True when the project is in the active lifecycle cohort (`active` /
   * `decision_needed`). Gates the switcher dropdown so it only appears once the
   * org+user has ≥1 active project — admins excepted (ANA-266).
   */
  active?: boolean;
  /** ISO timestamp for recency grouping in the home workspace picker. */
  updated_at?: string;
}

interface ProjectTopBarProps {
  currentProject: Project;
  projects?: Project[];
  user: UserInfo;
  onProjectSelect?: (projectId: string) => void;
  onCreateProject?: () => void;
  onLogOut?: () => void;
  orgs?: OrgInfo[];
  currentOrgId?: string | null;
  onOrgSwitch?: (orgId: string) => void;
  /**
   * 'project' (default): render the switcher whenever the active-project gate
   * allows it. 'home': additionally collapse to a static label when there is
   * exactly one project. In both cases the switcher only appears once an active
   * project exists — admins excepted. See {@link Project.active} (ANA-266).
   */
  variant?: "home" | "project";
  /**
   * When true, render the project name as a STATIC label (no switcher
   * dropdown) regardless of variant / project count — used by focused surfaces
   * like the scoping call where the user must stay on the bound project (017).
   * The logo-home link and user menu are unaffected.
   */
  lockProject?: boolean;
  /**
   * When true, render NO project switcher and NO static project label at all —
   * the centre of the bar is empty. Used by surfaces that aren't project-scoped
   * (e.g. the Admin Hub, where the `isAdmin` force-show would otherwise surface
   * the picker). The logo-home link and user menu are unaffected.
   */
  hideProjectSwitcher?: boolean;
  /**
   * Storybook / test escape hatch — forces the mobile burger menu open.
   * Production callers should leave this undefined; component owns its own
   * open/close state otherwise.
   */
  forceMobileMenuOpen?: boolean;
  isAdmin?: boolean;
  /**
   * Storybook / test escape hatch for the appearance toggle, forwarded to both
   * the desktop dropdown and the mobile inline profile. Production callers omit
   * these — the toggle reads ThemePreferenceContext instead.
   */
  themePreference?: ThemePreference;
  onThemePreferenceChange?: (value: ThemePreference) => void | Promise<void>;
  className?: string;
}

export function ProjectTopBar({
  currentProject,
  projects = [],
  user,
  onProjectSelect,
  onCreateProject,
  onLogOut,
  orgs,
  currentOrgId,
  onOrgSwitch,
  variant = "project",
  lockProject = false,
  hideProjectSwitcher = false,
  forceMobileMenuOpen,
  isAdmin = false,
  themePreference,
  onThemePreferenceChange,
  className,
}: ProjectTopBarProps) {
  // The project switcher appears once the org+user has ≥1 active project
  // (ANA-266) OR there are ≥2 projects to switch between — a switcher is for
  // switching, so multiple projects always warrant it (the home "show it when
  // there are multiple projects" rule). Admins always keep it. lockProject
  // still forces a static label; the home variant additionally collapses a
  // single project to a label.
  const hasActiveProject = projects.some((p) => p.active === true);
  const hasMultipleProjects = projects.length >= 2;
  const showDropdown =
    !hideProjectSwitcher &&
    !lockProject &&
    (isAdmin ||
      hasMultipleProjects ||
      (hasActiveProject && (variant !== "home" || projects.length !== 1)));
  // When the switcher is hidden and there's no real project to name (the
  // GlobalNavBar sentinel), render nothing rather than a placeholder pill.
  // `hideProjectSwitcher` also suppresses the static-label fallback entirely.
  const hasRealCurrentProject =
    !hideProjectSwitcher && currentProject.id !== NO_CURRENT_PROJECT_ID;
  // 046 — when the project is locked for a live call (scoping) AND there is a real
  // project to name, the mobile bar pins the project pill in place of the burger
  // (matching the handoff `SbMobileNav`). Today, under `lockProject`, the mobile
  // burger's switcher is suppressed with no fallback, so the founder sees NO
  // project identity on mobile at all — this closes that gap.
  const mobileLocked = lockProject && hasRealCurrentProject;
  const [internalMenuOpen, setInternalMenuOpen] = useState(false);
  const mobileMenuOpen = forceMobileMenuOpen ?? internalMenuOpen;

  // Brand wordmark shows the current organisation's name, falling back to the
  // product name while orgs load or when the user has none.
  const currentOrgName = orgs?.find((o) => o.id === currentOrgId)?.name;
  const brandLabel = currentOrgName || "Anaka";
  // Org switcher appears only when there's something to switch between — mirrors
  // the desktop UserProfileMenu gate (orgs.length > 1).
  const showOrgSwitcher = !!orgs && orgs.length > 1;

  const projectsByRecency = useMemo(() => {
    const map = new Map<ProjectRecencyBucket, Project[]>();
    for (const b of BUCKET_ORDER) map.set(b, []);
    for (const p of projects) {
      const bucket = bucketProjectUpdatedAt(p.updated_at);
      map.get(bucket)!.push(p);
    }
    return map;
  }, [projects]);

  /**
   * Project switcher dropdown content — reused by both the desktop trigger
   * and the mobile burger panel so the picker UX stays in lock-step.
   */
  const projectSwitcherMenu = (
    <DropdownMenuContent
      align="center"
      className="w-[270px] rounded-[10px] border-border/50 bg-popover p-0.5"
    >
      {BUCKET_ORDER.map((bucket) => {
        const bucketProjects = projectsByRecency.get(bucket) ?? [];
        if (bucketProjects.length === 0) return null;
        return (
          <div key={bucket} className="py-0.5">
            <DropdownMenuLabel className="text-muted-foreground px-4 py-1.5 text-[11px] font-semibold uppercase tracking-wide">
              {PROJECT_BUCKET_LABEL[bucket]}
            </DropdownMenuLabel>
            {bucketProjects.map((project) => {
              const isSelected = project.id === currentProject.id;
              return (
                <DropdownMenuItem
                  key={project.id}
                  className={cn(
                    "flex h-[37px] cursor-pointer items-center gap-3 rounded-none px-4 text-sm",
                    isSelected
                      ? "bg-primary/15 font-semibold text-primary"
                      : "bg-linear-to-b from-muted/80 to-muted/60 font-light text-muted-foreground",
                  )}
                  onSelect={() => onProjectSelect?.(project.id)}
                >
                  <StatusDot status={isSelected ? "active" : "inactive"} />
                  <span className="truncate">{project.name}</span>
                </DropdownMenuItem>
              );
            })}
          </div>
        );
      })}
      {projects.length > 0 && (
        <DropdownMenuSeparator className="mx-0 bg-border/30" />
      )}
      <DropdownMenuItem
        className="flex h-[37px] cursor-pointer items-center gap-3 rounded-none bg-linear-to-b from-muted/80 to-muted/60 px-3 font-light text-muted-foreground"
        onSelect={() => onCreateProject?.()}
      >
        <Plus className="size-3 shrink-0" />
        <span>Create New Project</span>
      </DropdownMenuItem>
    </DropdownMenuContent>
  );

  return (
    <header
      className={cn(
        // `relative` so the mobile burger panel can anchor itself absolutely
        // beneath the bar (handoff app.css §MOBILE NAV — .nav.nav-mobile is
        // position: relative, .nav-mobile-panel is position: absolute).
        "glass-nav relative",
        // glass-nav applies backdrop-filter, which makes this <header> its own
        // stacking context — so the burger panel's z-index can't lift it above
        // the sibling <ProjectTabs> strip (also a glass-nav stacking context in
        // the same NavBar). Elevate the whole header while the menu is open so
        // the panel paints over the tabs instead of being covered by them.
        mobileMenuOpen && "z-50",
        className,
      )}
    >
      {/* ─── Desktop bar (md+) ─── */}
      <div className="hidden h-16 items-center justify-between px-6 md:flex">
        {/* Left: Logo → home */}
        <Link
          href="/"
          aria-label={`${brandLabel} home`}
          className={cn(
            "flex items-center gap-2.5 rounded-md outline-none transition-opacity hover:opacity-90",
            "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          )}
        >
          <img
            src="/assets/anaka_icon.png"
            alt=""
            className="size-8 rounded-md"
          />
          <span className="text-lg font-bold tracking-tight text-foreground truncate max-w-[260px]" aria-hidden>
            {brandLabel}
          </span>
        </Link>

        {/* Center: Project Picker */}
        {showDropdown ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                data-testid="project-dropdown-trigger"
                variant="ghost"
                className="h-auto max-w-[800px] gap-2 rounded-full border border-border bg-secondary px-3.5 py-1.5 text-[13px] font-medium text-foreground hover:bg-secondary/80"
              >
                <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-[#A855F7]" />
                <span className="truncate">{currentProject.name}</span>
                <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
              </Button>
            </DropdownMenuTrigger>
            {projectSwitcherMenu}
          </DropdownMenu>
        ) : hasRealCurrentProject ? (
          <span
            data-testid="project-static-label"
            className="inline-flex max-w-[800px] items-center gap-2 truncate rounded-full border border-border bg-secondary px-3.5 py-1.5 text-[13px] font-medium text-foreground"
          >
            <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-[#A855F7]" />
            <span className="truncate">{currentProject.name}</span>
            {/* 046 — the "fixed for the call" cue: a Lock glyph next to the name
                when (and only when) the project is locked, matching the handoff.
                Gated on `lockProject` so the ANA-266 static-label fallback (a
                non-locked page with no active project) stays lock-free. */}
            {lockProject ? (
              <Lock
                data-testid="project-locked-glyph"
                aria-hidden
                className="size-3 shrink-0 text-muted-foreground"
              />
            ) : null}
          </span>
        ) : null}

        {/* Right: User Profile */}
        <UserProfileMenu
          user={user}
          onLogOut={onLogOut}
          orgs={orgs}
          currentOrgId={currentOrgId}
          onOrgSwitch={onOrgSwitch}
          isAdmin={isAdmin}
          themePreference={themePreference}
          onThemePreferenceChange={onThemePreferenceChange}
        />
      </div>

      {/* ─── Mobile bar (<md) ─── 64px to match desktop. Default: logo-left,
           burger-right, no project pill. When the project is LOCKED for a call
           (scoping, 046) the burger is replaced by the handoff's pinned layout —
           app icon · centred locked-project pill (dot + name + Lock,
           non-interactive) · avatar → account menu. The founder can't switch
           project mid-call, so the burger's switcher would be dead weight; the
           avatar menu still carries org / appearance / settings / log out. */}
      <div className="flex h-16 items-center justify-between gap-2 px-4 md:hidden">
        <Link
          href="/"
          aria-label={`${brandLabel} home`}
          className={cn(
            "flex shrink-0 items-center rounded-md outline-none transition-opacity hover:opacity-90",
            "focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 focus-visible:ring-offset-background",
          )}
        >
          <img
            src="/assets/anaka_icon.png"
            alt=""
            className="size-8 rounded-md"
          />
        </Link>
        {mobileLocked ? (
          <>
            <span
              data-testid="project-topbar-mobile-locked-label"
              className="inline-flex min-w-0 flex-1 items-center justify-center gap-1.5 rounded-full border border-border bg-secondary px-3 py-1.5 text-[13px] font-medium text-foreground"
            >
              <span aria-hidden className="size-1.5 shrink-0 rounded-full bg-[#A855F7]" />
              <span className="truncate">{currentProject.name}</span>
              <Lock aria-hidden className="size-3 shrink-0 text-muted-foreground" />
            </span>
            <UserProfileMenu
              user={user}
              onLogOut={onLogOut}
              orgs={orgs}
              currentOrgId={currentOrgId}
              onOrgSwitch={onOrgSwitch}
              isAdmin={isAdmin}
              themePreference={themePreference}
              onThemePreferenceChange={onThemePreferenceChange}
              compact
            />
          </>
        ) : (
          <button
            type="button"
            data-testid="project-topbar-burger"
            aria-label="Menu"
            aria-expanded={mobileMenuOpen}
            onClick={() => setInternalMenuOpen((o) => !o)}
            className={cn(
              "inline-flex size-10 shrink-0 items-center justify-center rounded-[10px] text-foreground transition-colors hover:bg-accent",
              "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
            )}
          >
            {mobileMenuOpen ? (
              <X className="size-5" strokeWidth={2} />
            ) : (
              <Menu className="size-5" strokeWidth={2} />
            )}
          </button>
        )}
      </div>

      {/* ─── Mobile burger panel ─── floats over page content (handoff:
           position: absolute, top: 100%, shadow 0 8px 24px rgba(0,0,0,0.18)).
           Never shown in the locked layout (no burger to open it). */}
      {mobileMenuOpen && !mobileLocked && (
        <div
          data-testid="project-topbar-mobile-panel"
          className={cn(
            "absolute inset-x-0 top-full z-30 flex flex-col gap-4 border-b border-border px-4 pb-[18px] pt-[14px] md:hidden",
            // Opaque menu surface, NOT glass: as a floating panel over page
            // content, the translucent `glass-nav` background (90%/85% alpha +
            // blur) let the page read through it on mobile (ANA-198). `bg-popover`
            // is the solid surface token (white / dark navy); the explicit shadow
            // keeps the handoff's 0 8px 24px rgba(0,0,0,0.18) elevation.
            "bg-popover shadow-[0_8px_24px_rgba(0,0,0,0.18)]",
          )}
        >
          {showDropdown && (
            <section className="flex flex-col gap-1.5">
              <p className="px-2.5 pt-1 text-[10px] font-bold uppercase tracking-[0.10em] text-muted-foreground">
                Project
              </p>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="project-topbar-mobile-project-row"
                    className="flex w-full items-center gap-2.5 rounded-[10px] border border-border bg-secondary px-3 py-2.5 text-left text-[13px] font-medium text-foreground transition-colors hover:bg-accent"
                  >
                    <StatusDot status="active" />
                    <span className="flex-1 truncate">{currentProject.name}</span>
                    <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                {projectSwitcherMenu}
              </DropdownMenu>
            </section>
          )}

          {showOrgSwitcher && (
            <section className="flex flex-col gap-1.5">
              <p className="px-2.5 pt-1 text-[10px] font-bold uppercase tracking-[0.10em] text-muted-foreground">
                Organisation
              </p>
              {/* Picker (trigger → dropdown), mirroring the Project switcher
                  above — desktop shows orgs as a flat list, mobile collapses
                  them behind a picker so the burger panel stays compact. */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    data-testid="project-topbar-mobile-org-row"
                    className="flex w-full items-center gap-2.5 rounded-[10px] border border-border bg-secondary px-3 py-2.5 text-left text-[13px] font-medium text-foreground transition-colors hover:bg-accent"
                  >
                    <Building2 className="size-3.5 shrink-0 text-muted-foreground" />
                    <span className="flex-1 truncate">
                      {currentOrgName ?? "Select organisation"}
                    </span>
                    <ChevronDown className="size-3.5 shrink-0 text-muted-foreground" />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  // Match the picker trigger's full width (Radix exposes the
                  // measured trigger width as this CSS var) instead of a fixed
                  // 270px, so the menu lines up with the Organisation row.
                  className="w-[var(--radix-dropdown-menu-trigger-width)] rounded-[10px] border-border/50 bg-popover p-0.5"
                >
                  {orgs!.map((org) => {
                    const isSelected = org.id === currentOrgId;
                    return (
                      <DropdownMenuItem
                        key={org.id}
                        className={cn(
                          "flex h-[37px] cursor-pointer items-center gap-3 rounded-none px-4 text-sm",
                          isSelected
                            ? "bg-primary/15 font-semibold text-primary"
                            : "bg-linear-to-b from-muted/80 to-muted/60 font-light text-muted-foreground",
                        )}
                        onSelect={() => onOrgSwitch?.(org.id)}
                      >
                        <span className="flex size-3 shrink-0 items-center justify-center">
                          {isSelected && <Check className="size-3" aria-hidden />}
                        </span>
                        <span className="truncate">{org.name}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            </section>
          )}

          {/* Account: the SAME UserProfileMenu the desktop bar renders, in its
              inline variant — one component, identical options (appearance,
              admin, log out) across breakpoints. */}
          <UserProfileMenu
            variant="inline"
            user={user}
            isAdmin={isAdmin}
            themePreference={themePreference}
            onThemePreferenceChange={onThemePreferenceChange}
            onLogOut={() => {
              setInternalMenuOpen(false);
              onLogOut?.();
            }}
          />
        </div>
      )}
    </header>
  );
}
